import React, { useEffect, useState } from "react";
import axiosInstance from "../../services/auth";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import { useCombinedContext } from "../../context/CombinedContext";
import * as XLSX from "xlsx";
import { Link } from "react-router-dom";

const PurchaseReport = () => {
  const [stocks, setStocks] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [selectedStockId, setSelectedStockId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));
  const userRole = authData.role;

  const { selectedCompany } = useCombinedContext();
  const companyId = selectedCompany?._id;

  const formatPrice = (price) => {
    const num = parseFloat(price);
    if (isNaN(num)) return "0.00";
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    const date = new Date(dateValue);
    return date.toLocaleDateString("en-GB");
  };

  const fetchStocks = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/logistics/stock/${companyId}`);
      const stocksArray = response.data.stocks || [];
      setStocks(stocksArray);
    } catch (error) {
      toast.error("Error fetching stocks");
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseReport = async () => {
    try {
      if (!companyId) return;

      if (!selectedStockId) {
        setPurchaseData([]);
        setFilterData([]);
        return;
      }

      setLoading(true);

      const params = {
        stockId: selectedStockId,
      };

      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;

      const response = await axiosInstance.get(
        `/logistics/purchase/report/${companyId}`,
        {
          params,
        },
      );

      const data = response.data.data || [];
      const sortedData = [...data].sort(
        (a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate),
      );

      setPurchaseData(sortedData);

      if (searchText) {
        const filtered = sortedData.filter(
          (item) =>
            item.invoiceNo?.toLowerCase().includes(searchText) ||
            item.shopName?.toLowerCase().includes(searchText) ||
            item.stockName?.toLowerCase().includes(searchText),
        );
        setFilterData(filtered);
      } else {
        setFilterData(sortedData);
      }
    } catch (error) {
      toast.error("Error fetching purchase report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchStocks();
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId && selectedStockId) {
      fetchPurchaseReport();
    }
  }, [companyId, selectedStockId, fromDate, toDate]);

  const handleSearchChange = (e) => {
    const text = e.target.value.toLowerCase();
    setSearchText(text);

    const filtered = purchaseData.filter(
      (item) =>
        item.invoiceNo?.toLowerCase().includes(text) ||
        item.shopName?.toLowerCase().includes(text) ||
        item.stockName?.toLowerCase().includes(text),
    );
    setFilterData(filtered);
    setCurrentPage(0);
  };

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  const currentItems = [...filterData].slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );

  const pageCount = Math.ceil(filterData.length / itemsPerPage);

  const handleExport = () => {
    if (!filterData || filterData.length === 0) {
      toast.info("No data to export");
      return;
    }

    const selectedStock = stocks.find((stock) => stock._id === selectedStockId);

    const sheetData = [];
    const merges = [];

    const currentDate = new Date();
    const reportDate = currentDate.toLocaleDateString();

    sheetData.push([`Report Generated Date: ${reportDate}`]);
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } });

    sheetData.push([
      `Product Name: ${selectedStock?.name || "All"}`,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
    merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 7 } });

    sheetData.push([
      `From Date: ${fromDate || "-"}`,
      "",
      "",
      "",
      "",
      "",
      "",
      "",
    ]);
    merges.push({ s: { r: 2, c: 0 }, e: { r: 2, c: 7 } });

    sheetData.push([`To Date: ${toDate || "-"}`, "", "", "", "", "", "", ""]);
    merges.push({ s: { r: 3, c: 0 }, e: { r: 3, c: 7 } });

    sheetData.push([
      "Sl No",
      "Invoice No",
      "Shop Name",
      "Product Name",
      "Invoice Date",
      "Quantity",
      "Unit Price",
      "Amount",
    ]);

    filterData.forEach((item, index) => {
      sheetData.push([
        index + 1,
        item.invoiceNo || "",
        item.shopName || "",
        item.stockName || "",
        item.invoiceDate ? formatDate(item.invoiceDate) : "",
        item.quantity ?? 0,
        item.unitPrice ?? 0,
        item.amount ?? 0,
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    worksheet["!merges"] = merges;

    worksheet["!cols"] = [
      { wch: 8 },
      { wch: 18 },
      { wch: 25 },
      { wch: 25 },
      { wch: 15 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase Report");

    XLSX.writeFile(
      workbook,
      `purchase_report_${selectedStock?.name || "all"}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`,
    );
  };

  return (
    <div>
      <ToastContainer />
      <Link to="/logistics/purchaselist">
        <button className="linkBtn mb-3">
          <i className="bi bi-arrow-left me-2"></i>Back to Purchase List
        </button>
      </Link>
      <div className="mb-4 headStyle d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Purchase Item Report</h4>
      </div>

      <div
        className="d-flex flex-wrap mt-3 align-items-center gap-3"
        style={{
          padding: "16px",
          boxShadow: "0px 0px 10px 0px #e0e0e0",
          background: "white",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label htmlFor="stockId" style={{ width: "-webkit-fill-available" }}>
            Product Name
          </label>
          <select
            className="form-select"
            value={selectedStockId}
            onChange={(e) => {
              setSelectedStockId(e.target.value);
              setCurrentPage(0);
            }}
            style={{
              border: "1px solid #1E2A5E",
              borderRadius: "8px",
              minWidth: "220px",
            }}
          >
            <option value="">Select Product</option>
            {stocks.map((stock) => (
              <option key={stock._id} value={stock._id}>
                {stock.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label htmlFor="fromDate" style={{ width: "-webkit-fill-available" }}>
            From Date
          </label>
          <input
            className="form-control"
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setCurrentPage(0);
            }}
            style={{
              border: "1px solid #1E2A5E",
              borderRadius: "8px",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <label htmlFor="toDate" style={{ width: "-webkit-fill-available" }}>
            To Date
          </label>
          <input
            className="form-control"
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setCurrentPage(0);
            }}
            style={{
              border: "1px solid #1E2A5E",
              borderRadius: "8px",
            }}
          />
        </div>
      </div>

      <div className="headStyle mt-4">
        <div className="d-flex mt-2 flex-wrap align-items-center gap-3 justify-content-between">
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div>
              <input
                className="form-control"
                type="search"
                placeholder="Search..."
                style={{
                  maxWidth: "100%",
                  border: "1px solid #1E2A5E",
                  borderRadius: "8px",
                }}
                onChange={handleSearchChange}
                value={searchText}
              />
            </div>

            <div className="d-flex align-items-center">
              <label className="me-2 pt-1">Count</label>
              <select
                className="form-select no-arrow"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(0);
                }}
                style={{ width: "auto" }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
                <option value="25">25</option>
                <option value="30">30</option>
              </select>
            </div>
          </div>
          {["admin", "superadmin"].includes(userRole) && (
            <div>
              <button className="button-2 exxportBtn" onClick={handleExport}>
                Export to Excel
              </button>
            </div>
          )}
        </div>

        <div
          className="my-3 rounded table-responsive-sm"
          style={{ overflowX: "auto" }}
        >
          <table id="my-table" className="table table-bordered mb-0">
            <thead className="t_head">
              <tr className="text-center align-middle">
                <th>Sl No</th>
                <th>Invoice No</th>
                <th>Shop Name</th>
                <th>Product Name</th>
                <th>Invoice Date</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Amount</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8">
                    <div className="d-flex justify-content-center m-3">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : !selectedStockId ? (
                <tr>
                  <td colSpan="8">
                    <div className="d-flex justify-content-center m-2">
                      <h6>Please select a product</h6>
                    </div>
                  </td>
                </tr>
              ) : currentItems.length === 0 ? (
                <tr>
                  <td colSpan="8">
                    <div className="d-flex justify-content-center m-2">
                      <h6>Data Not Found</h6>
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((item, index) => (
                  <tr key={index}>
                    <td className="text-center">
                      {currentPage * itemsPerPage + index + 1}
                    </td>
                    <td>{item.invoiceNo || "-"}</td>
                    <td>{item.shopName || "-"}</td>
                    <td>{item.stockName || "-"}</td>
                    <td>{formatDate(item.invoiceDate)}</td>
                    <td className="text-end">{item.quantity ?? 0}</td>
                    <td className="text-end">{formatPrice(item.unitPrice)}</td>
                    <td className="text-end">{formatPrice(item.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <ReactPaginate
          breakLabel="..."
          nextLabel="next >"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="< previous"
          renderOnZeroPageCount={null}
          containerClassName="pagination justify-content-center pagination-sm"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          activeClassName="active"
          previousClassName="page-item"
          previousLinkClassName="page-link"
          nextClassName="page-item"
          nextLinkClassName="page-link"
          disabledClassName="disabled"
        />
      </div>
    </div>
  );
};

export default PurchaseReport;
